/**
 * Minimal ZIP writer — enough to package a page template folder, nothing more.
 *
 * Why hand-rolled rather than JSZip: the admin ships no zip library today, and
 * pulling one in for four small text files is a poor trade. Entries are STORED
 * (no compression), which is a valid, universally readable ZIP and removes the
 * only genuinely tricky part of the format. The files are ~100 KB of text that
 * the browser will gzip over the wire anyway, so compression buys nothing here.
 *
 * Only what a template needs is implemented: no directory entries, no ZIP64,
 * no encryption, no multi-disk. Anything larger than 4 GB or more than 65,535
 * files would need ZIP64 — a template is four files.
 */

const LOCAL_HEADER_SIG   = 0x04034b50;
const CENTRAL_HEADER_SIG = 0x02014b50;
const END_OF_CENTRAL_SIG = 0x06054b50;

// Bit 11 tells the reader that names are UTF-8 rather than the legacy CP437,
// so a non-ASCII slug extracts with the right characters.
const FLAG_UTF8_NAMES = 0x800;

let crcTable = null;

function getCrcTable() {
  if (crcTable) return crcTable;
  crcTable = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    crcTable[i] = c >>> 0;
  }
  return crcTable;
}

export function crc32(bytes) {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** MS-DOS date/time, which is what the format stores. */
function dosDateTime(date) {
  const time =
    (Math.floor(date.getSeconds() / 2)) |
    (date.getMinutes() << 5) |
    (date.getHours() << 11);
  const day =
    date.getDate() |
    ((date.getMonth() + 1) << 5) |
    ((date.getFullYear() - 1980) << 9);
  return { time, day };
}

/**
 * Build a ZIP from [{ name, content }] entries, where `name` may contain "/"
 * to place the file inside a folder.
 *
 * Returns raw bytes rather than a Blob so the packing logic stays testable on
 * its own — the caller wraps it for the download.
 *
 * @returns {Uint8Array}
 */
export function buildZip(entries, now = new Date()) {
  const encoder = new TextEncoder();
  const { time, day } = dosDateTime(now);

  const parts = [];        // local headers + data, in order
  const central = [];      // central directory records
  let offset = 0;

  entries.forEach((entry) => {
    const nameBytes = encoder.encode(entry.name);
    const dataBytes = encoder.encode(entry.content ?? '');
    const crc = crc32(dataBytes);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, LOCAL_HEADER_SIG, true);
    local.setUint16(4, 20, true);               // version needed
    local.setUint16(6, FLAG_UTF8_NAMES, true);
    local.setUint16(8, 0, true);                // method 0 = stored
    local.setUint16(10, time, true);
    local.setUint16(12, day, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, dataBytes.length, true); // compressed size
    local.setUint32(22, dataBytes.length, true); // uncompressed size
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true);                // extra field length

    parts.push(new Uint8Array(local.buffer), nameBytes, dataBytes);

    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, CENTRAL_HEADER_SIG, true);
    cd.setUint16(4, 20, true);                  // version made by
    cd.setUint16(6, 20, true);                  // version needed
    cd.setUint16(8, FLAG_UTF8_NAMES, true);
    cd.setUint16(10, 0, true);
    cd.setUint16(12, time, true);
    cd.setUint16(14, day, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, dataBytes.length, true);
    cd.setUint32(24, dataBytes.length, true);
    cd.setUint16(28, nameBytes.length, true);
    cd.setUint16(30, 0, true);                  // extra
    cd.setUint16(32, 0, true);                  // comment
    cd.setUint16(34, 0, true);                  // disk number
    cd.setUint16(36, 0, true);                  // internal attrs
    cd.setUint32(38, 0, true);                  // external attrs
    cd.setUint32(42, offset, true);             // offset of local header

    central.push(new Uint8Array(cd.buffer), nameBytes);

    offset += 30 + nameBytes.length + dataBytes.length;
  });

  const centralSize = central.reduce((n, chunk) => n + chunk.length, 0);

  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, END_OF_CENTRAL_SIG, true);
  end.setUint16(4, 0, true);                    // this disk
  end.setUint16(6, 0, true);                    // disk with central dir
  end.setUint16(8, entries.length, true);       // entries on this disk
  end.setUint16(10, entries.length, true);      // entries total
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);              // central dir offset
  end.setUint16(20, 0, true);                   // comment length

  const chunks = [...parts, ...central, new Uint8Array(end.buffer)];
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  chunks.forEach((c) => { out.set(c, at); at += c.length; });
  return out;
}
